import { useEffect, useState } from "react";
import axios from "axios";
import { api } from "./api";

export type UserRole = "central" | "admin";

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  mobile?: string;
  full_mobile?: string;
  role: UserRole;
  central_id?: number;
  central_name?: string;
  portal_role?: string;
  permissions?: string[];
}

const TOKEN_KEY = "sij_token";
const USER_KEY = "sij_user";

/**
 * Fired on the window whenever the stored auth user changes (login, logout,
 * validateSession refresh, or impersonation). The `detail` carries the new
 * `AuthUser | null`. `useAuthUser` listens for this (plus the cross-tab
 * `storage` event) so components re-render reactively — mirrors the
 * custom-event pattern in `lib/prefs.ts`.
 */
export const AUTH_CHANGE_EVENT = "sij:auth-change";

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT, { detail: null }));
  }
}

export async function logout(): Promise<void> {
  const token = getStoredToken();
  if (token) {
    try {
      await api.post("/auth/logout");
    } catch {
      /* server logout optional */
    }
  }
  clearAuth();
}

export function persistAuth(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT, { detail: user }));
  }
}

/**
 * React hook returning the current stored auth user, re-rendering reactively on
 * auth changes from this tab (custom `AUTH_CHANGE_EVENT`) or other tabs (the
 * `storage` event). Mirrors `useNumeralsPref` in `lib/prefs.ts`. Non-React
 * callers keep using the synchronous `getStoredUser()`.
 */
export function useAuthUser(): AuthUser | null {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  useEffect(() => {
    const sync = () => setUser(getStoredUser());
    window.addEventListener(AUTH_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return user;
}

export function homePathForRole(role: UserRole): string {
  return role === "admin" ? "/admin" : "/";
}

/**
 * Check if the current user has a specific permission.
 * Super Admins (portal_role === "Super Admin") always return true.
 */
export function hasPermission(user: AuthUser | null, permission: string): boolean {
  if (!user) return false;
  if (user.portal_role === "Super Admin") return true;
  return user.permissions?.includes(permission) ?? false;
}

/**
 * React hook that returns true if the current user has the given permission.
 */
export function useCan(permission: string): boolean {
  const user = useAuthUser();
  return hasPermission(user, permission);
}

/**
 * React hook that returns true if the current user's role **or** portal_role
 * matches any value in the provided list.
 *
 * Accepts both system roles ("central", "admin") and portal roles
 * ("Moderators", "Super Admin", …).
 * Super Admins (portal_role === "Super Admin") always return true.
 *
 * @example
 * const isAdmin      = useHasRole(["admin"]);
 * const isModerator  = useHasRole(["Moderators"]);
 */
export function useHasRole(roles: string[]): boolean {
  const user = useAuthUser();
  if (!user) return false;
  // if (user.portal_role === "Super Admin") return true;
  return roles.includes(user.role) || (!!user.portal_role && roles.includes(user.portal_role));
}

/**
 * React hook that returns true if the current user holds **all** of the
 * specified permissions.
 *
 * Super Admins (portal_role === "Super Admin") always return true.
 *
 * @example
 * const canManageReviewers = useHasPermissions(["reviewers.view", "reviewers.edit"]);
 */
export function useHasPermissions(permissions: string[]): boolean {
  const user = useAuthUser();
  if (!user) return false;
  if (user.portal_role === "Super Admin") return true;
  return permissions.every((p) => user.permissions?.includes(p) ?? false);
}

type ApiLoginResponseSuccess = { token: string; user: AuthUser };
type ApiLoginResponseOtp = { requires_otp: true; login_token: string };
type ApiLoginResponseEnvelope = { data: ApiLoginResponseSuccess };

type ApiLoginResponse = ApiLoginResponseSuccess | ApiLoginResponseOtp | ApiLoginResponseEnvelope;

export async function login(
  email: string,
  password: string,
  portal: UserRole,
): Promise<{ token: string; user: AuthUser } | { requiresOtp: true; loginToken: string }> {
  const path = portal === "admin" ? "/admin/login" : "/central/login";
  const { data: raw } = await api.post<any>(path, { email, password });
  const data = raw.data ?? raw;

  if (data.requires_otp && data.login_token) {
    return { requiresOtp: true, loginToken: data.login_token };
  }

  if (!data.token || !data.user) throw new Error("Invalid login response");

  persistAuth(data.token, data.user);
  return { token: data.token, user: data.user };
}

export async function adminLoginVerify(
  loginToken: string,
  code: string,
): Promise<{ token: string; user: AuthUser }> {
  const { data } = await api.post<{ token: string; user: AuthUser }>("/admin/login/verify", {
    login_token: loginToken,
    code,
  });
  persistAuth(data.token, data.user);
  return data;
}

export async function adminLoginResendOtp(loginToken: string): Promise<void> {
  await api.post("/admin/login/resend-otp", { login_token: loginToken });
}

export async function centralLoginVerify(
  loginToken: string,
  code: string,
): Promise<{ token: string; user: AuthUser }> {
  const { data: raw } = await api.post<any>("/central/login/verify", {
    login_token: loginToken,
    code,
  });
  const data = raw.data ?? raw;
  persistAuth(data.token, data.user);
  return data;
}

export async function centralLoginResendOtp(loginToken: string): Promise<void> {
  await api.post("/central/login/resend-otp", { login_token: loginToken });
}

export async function centralForgotPassword(email: string): Promise<void> {
  await api.post("/central/forgot-password", { email });
}

export async function centralVerifyOtp(email: string, code: string): Promise<void> {
  await api.post("/central/verify-otp", { email, code });
}

export async function centralResetPassword(email: string, code: string, password: string): Promise<void> {
  await api.post("/central/reset-password", { email, code, password });
}

export async function validateSession(): Promise<boolean> {
  const token = getStoredToken();
  if (!token) return false;

  const storedUser = getStoredUser();
  const path = storedUser?.role === "admin" ? "/admin/me" : "/central/me";
  try {
    const { data: raw } = await api.get<{ user?: AuthUser; data?: { user: AuthUser } }>(path);
    const user = raw.data?.user ?? raw.user;
    if (user) persistAuth(token, user);
    return true;
  } catch (e) {
    // Only a genuine auth rejection should nuke the session. A 404 (wrong base
    // path), CORS failure, or 5xx looks identical otherwise and silently bounces
    // the user to /login, hiding the real cause.
    if (axios.isAxiosError(e)) {
      const status = e.response?.status;
      console.error(
        `[validateSession] GET ${path} failed:`,
        status ? `HTTP ${status}` : e.code ?? "network/CORS error (no response)",
        { url: e.config?.baseURL ?? "" + (e.config?.url ?? ""), data: e.response?.data },
      );
      if (status === 401 || status === 403) {
        clearAuth();
        return false;
      }
      // Non-auth failure: keep the token so the real error stays visible.
      return false;
    }
    console.error("[validateSession] unexpected error:", e);
    return false;
  }
}
